import {
    parseSixyncEvent,
    parseSixyncSnapshot,
    SixyncCueCancelledPayloadSchema,
    SixyncCueStartedPayloadSchema,
    SixyncPerceptionProfilePayloadSchema,
    SixyncSceneLayoutSetPayloadSchema,
    SixyncSceneLayerRemovedPayloadSchema,
    SixyncSceneLayerSetPayloadSchema,
    SixyncSourceEventPayloadSchema,
    SixyncSurfacePayloadSchema,
    type SixyncEvent,
    type SixyncOperation,
    type SixyncSnapshot,
    type SixyncCue,
    type SixyncSourceStatus,
} from './Sixync';

export type SixyncApplyEventStatus =
    | 'applied'
    | 'stale'
    | 'gap'
    | 'invalid_context'
    | 'unsupported';

export interface SixyncApplyEventResult {
    status: SixyncApplyEventStatus;
    snapshot: SixyncSnapshot;
    expectedSeq: number;
    eventSeq: number;
    reason?: string;
}

const sourceStatusByOperation: Partial<Record<SixyncOperation, SixyncSourceStatus>> = {
    source_announced: 'announced',
    source_ready: 'ready',
    source_stalled: 'stalled',
    source_stopped: 'stopped',
};

export const getNextSixyncSeq = (snapshot: SixyncSnapshot): number => (
    snapshot.snapshotVersion + 1
);

export const canApplySixyncEvent = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
): SixyncApplyEventResult | null => {
    const expectedSeq = getNextSixyncSeq(snapshot);

    if (
        snapshot.roomId !== event.roomId ||
        snapshot.sessionId !== event.sessionId ||
        (event.roomSessionId && snapshot.roomSessionId !== event.roomSessionId)
    ) {
        return {
            status: 'invalid_context',
            snapshot,
            expectedSeq,
            eventSeq: event.seq,
            reason: 'event room/session does not match snapshot',
        };
    }

    if (event.seq <= snapshot.snapshotVersion) {
        return {
            status: 'stale',
            snapshot,
            expectedSeq,
            eventSeq: event.seq,
            reason: 'event sequence is already included in snapshot',
        };
    }

    if (event.seq !== expectedSeq) {
        return {
            status: 'gap',
            snapshot,
            expectedSeq,
            eventSeq: event.seq,
            reason: 'event sequence does not match expected next sequence',
        };
    }

    return null;
};

const withSnapshotVersion = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
    updates: Partial<SixyncSnapshot>,
): SixyncSnapshot => parseSixyncSnapshot({
    ...snapshot,
    ...updates,
    snapshotVersion: event.seq,
    serverTime: event.serverTime,
});

const withSceneVersion = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
    updates: Partial<SixyncSnapshot>,
): SixyncSnapshot => {
    const sceneVersion = event.seq;

    return withSnapshotVersion(snapshot, event, {
        ...updates,
        sceneVersion,
        scene: updates.scene ?? {
            ...snapshot.scene,
            version: sceneVersion,
        },
    });
};

const uniqueSourceIds = (sourceIds: string[]): string[] => (
    Array.from(new Set(sourceIds))
);

const applySourceEvent = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
): SixyncSnapshot => {
    const payload = SixyncSourceEventPayloadSchema.parse(event.payload);
    const status = sourceStatusByOperation[event.op] ?? 'announced';
    const existingSource = snapshot.sources[payload.sourceId];
    const source = payload.source
        ? {
            ...payload.source,
            status,
        }
        : existingSource
            ? {
                ...existingSource,
                status,
                stoppedAt: status === 'stopped' ? event.serverTime : existingSource.stoppedAt,
                readyAt: status === 'ready' ? event.serverTime : existingSource.readyAt,
            }
            : {
                sourceId: payload.sourceId,
                kind: 'activity_surface' as const,
                status,
            };

    const sources = status === 'stopped'
        ? {
            ...snapshot.sources,
            [payload.sourceId]: source,
        }
        : {
            ...snapshot.sources,
            [payload.sourceId]: source,
        };

    const activeSourceIds = status === 'stopped'
        ? snapshot.activeSourceIds.filter((sourceId) => sourceId !== payload.sourceId)
        : uniqueSourceIds([
            ...snapshot.activeSourceIds,
            payload.sourceId,
        ]);

    return withSnapshotVersion(snapshot, event, {
        sources,
        activeSourceIds,
    });
};

const applySceneLayerSet = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
): SixyncSnapshot => {
    const { layer } = SixyncSceneLayerSetPayloadSchema.parse(event.payload);
    const layers = [
        ...snapshot.scene.layers.filter((existingLayer) => existingLayer.layerId !== layer.layerId),
        layer,
    ].sort((left, right) => left.zIndex - right.zIndex);

    return withSceneVersion(snapshot, event, {
        scene: {
            ...snapshot.scene,
            version: event.seq,
            layers,
        },
    });
};

const applySceneLayoutSet = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
): SixyncSnapshot => {
    const { layout } = SixyncSceneLayoutSetPayloadSchema.parse(event.payload);
    const currentRevision = snapshot.scene.programLayout?.revision;

    if (typeof currentRevision === 'number' && layout.revision <= currentRevision) {
        return withSnapshotVersion(snapshot, event, {});
    }

    return withSceneVersion(snapshot, event, {
        scene: {
            ...snapshot.scene,
            version: event.seq,
            programLayout: layout,
        },
    });
};

const applySceneLayerRemoved = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
): SixyncSnapshot => {
    const { layerId } = SixyncSceneLayerRemovedPayloadSchema.parse(event.payload);

    return withSceneVersion(snapshot, event, {
        scene: {
            ...snapshot.scene,
            version: event.seq,
            layers: snapshot.scene.layers.filter((layer) => layer.layerId !== layerId),
        },
    });
};

const applySurfaceEvent = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
): SixyncSnapshot => {
    const { surface } = SixyncSurfacePayloadSchema.parse(event.payload);
    const ready = event.op === 'surface_ready';

    return withSceneVersion(snapshot, event, {
        targetSurfaces: {
            ...snapshot.targetSurfaces,
            [surface.surfaceId]: {
                ...surface,
                ready,
                readySeq: ready ? event.seq : surface.readySeq,
                lastChangedSeq: event.seq,
            },
        },
    });
};

const applyCueStarted = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
): SixyncSnapshot => {
    const { cue } = SixyncCueStartedPayloadSchema.parse(event.payload);
    // A CFB cue is a dispatch observation, never a command to replay speech.
    // Keep cancelled identities and reject callbacks from an obsolete position.
    if (cue.payload?.cfbNarration !== undefined) {
        const narration = cue.payload.cfbNarration as { watchSessionId: string; generation: number; gameId: number };
        const playback = cue.sourceId ? snapshot.sources[cue.sourceId]?.metadata?.cfbPlayback as {
            watchSessionId: string; generation: number; gameId: number; status: string; paused: boolean;
        } | undefined : undefined;
        if (!playback || playback.watchSessionId !== narration.watchSessionId ||
            playback.generation !== narration.generation || playback.gameId !== narration.gameId ||
            playback.status !== 'ready' || playback.paused || snapshot.cues[cue.cueId]?.status === 'cancelled') {
            return withSnapshotVersion(snapshot, event, {});
        }
    }
    const supersededCueIds = getSupersededAgentVisemeCueIds(snapshot, cue);
    if (cue.kind === 'telestration_stroke' && !snapshot.cues[cue.cueId]) {
        const ink = Object.values(snapshot.cues)
            .filter((item) => item.kind === 'telestration_stroke')
            .sort((a, b) => a.startedAt - b.startedAt || a.cueId.localeCompare(b.cueId));
        for (const item of ink.slice(0, Math.max(0, ink.length - 63))) supersededCueIds.add(item.cueId);
    }
    const nextCues = Object.fromEntries(
        Object.entries(snapshot.cues).filter(([cueId]) => !supersededCueIds.has(cueId)),
    );

    return withSceneVersion(snapshot, event, {
        cues: {
            ...nextCues,
            [cue.cueId]: cue,
        },
        activeCueIds: uniqueSourceIds([
            ...snapshot.activeCueIds.filter((cueId) => (
                cueId !== cue.cueId && !supersededCueIds.has(cueId)
            )),
            cue.cueId,
        ]),
    });
};

// Mouth-drive cue kinds are per-companion singletons: a new utterance's cue
// supersedes the previous one of the SAME kind for that companion.
// agent_viseme_sequence and companion_mouth_track coexist independently.
const AGENT_MOUTH_CUE_KINDS: ReadonlySet<string> = new Set([
    'agent_viseme_sequence',
    'companion_mouth_track',
]);

const getSupersededAgentVisemeCueIds = (
    snapshot: SixyncSnapshot,
    cue: SixyncCue,
): Set<string> => {
    if (!AGENT_MOUTH_CUE_KINDS.has(cue.kind)) {
        return new Set();
    }
    const companionId = typeof cue.payload?.companionId === 'string'
        ? cue.payload.companionId
        : undefined;
    if (!companionId) {
        return new Set();
    }

    return new Set(Object.entries(snapshot.cues)
        .filter(([cueId, existingCue]) => (
            cueId !== cue.cueId &&
            existingCue.kind === cue.kind &&
            existingCue.payload?.companionId === companionId
        ))
        .map(([cueId]) => cueId));
};

const applyCueCancelled = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
): SixyncSnapshot => {
    const { cueId } = SixyncCueCancelledPayloadSchema.parse(event.payload);
    const existingCue = snapshot.cues[cueId];

    if (existingCue?.kind === 'telestration_stroke') {
        const cues = { ...snapshot.cues };
        delete cues[cueId];
        return withSceneVersion(snapshot, event, {
            cues,
            activeCueIds: snapshot.activeCueIds.filter((id) => id !== cueId),
        });
    }

    return withSceneVersion(snapshot, event, {
        cues: existingCue
            ? {
                ...snapshot.cues,
                [cueId]: {
                    ...existingCue,
                    status: 'cancelled',
                    endsAt: event.serverTime,
                },
            }
            : snapshot.cues,
        activeCueIds: snapshot.activeCueIds.filter((activeCueId) => activeCueId !== cueId),
    });
};

const applyPerceptionProfileSet = (
    snapshot: SixyncSnapshot,
    event: SixyncEvent,
): SixyncSnapshot => {
    const { perceptionProfileId } = SixyncPerceptionProfilePayloadSchema.parse(event.payload);

    return withSceneVersion(snapshot, event, {
        perceptionProfileId,
    });
};

export const applySixyncEventToSnapshot = (
    snapshotInput: SixyncSnapshot,
    eventInput: SixyncEvent,
): SixyncApplyEventResult => {
    const snapshot = parseSixyncSnapshot(snapshotInput);
    const event = parseSixyncEvent(eventInput);
    const preflightResult = canApplySixyncEvent(snapshot, event);

    if (preflightResult) {
        return preflightResult;
    }

    const expectedSeq = getNextSixyncSeq(snapshot);
    let nextSnapshot: SixyncSnapshot;

    switch (event.op) {
        case 'source_announced':
        case 'source_ready':
        case 'source_stalled':
        case 'source_stopped':
            nextSnapshot = applySourceEvent(snapshot, event);
            break;
        case 'scene_layout_set':
            nextSnapshot = applySceneLayoutSet(snapshot, event);
            break;
        case 'scene_layer_set':
            nextSnapshot = applySceneLayerSet(snapshot, event);
            break;
        case 'scene_layer_removed':
            nextSnapshot = applySceneLayerRemoved(snapshot, event);
            break;
        case 'surface_ready':
        case 'surface_unready':
            nextSnapshot = applySurfaceEvent(snapshot, event);
            break;
        case 'cue_started':
            nextSnapshot = applyCueStarted(snapshot, event);
            break;
        case 'cue_cancelled':
            nextSnapshot = applyCueCancelled(snapshot, event);
            break;
        case 'perception_profile_set':
            nextSnapshot = applyPerceptionProfileSet(snapshot, event);
            break;
        case 'intent_proposed':
        case 'intent_accepted':
        case 'intent_rejected':
            nextSnapshot = withSnapshotVersion(snapshot, event, {});
            break;
        default:
            return {
                status: 'unsupported',
                snapshot,
                expectedSeq,
                eventSeq: event.seq,
                reason: `unsupported operation ${event.op satisfies never}`,
            };
    }

    return {
        status: 'applied',
        snapshot: nextSnapshot,
        expectedSeq,
        eventSeq: event.seq,
    };
};
